import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { FiMail, FiLock, FiEye, FiEyeOff } from 'react-icons/fi';

function NetworkBackground() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const particles = [];
    const mouse = { x: -1000, y: -1000, active: false };
    let animationFrame;
    let width = 0;
    let height = 0;
    let dpr = 1;

    const createParticles = () => {
      particles.length = 0;
      const spacing = Math.max(82, Math.min(132, width / 12));
      const columns = Math.ceil(width / spacing) + 2;
      const rows = Math.ceil(height / spacing) + 2;

      for (let row = 0; row < rows; row += 1) {
        for (let column = 0; column < columns; column += 1) {
          const offsetX = row % 2 === 0 ? spacing * 0.18 : spacing * 0.58;

          particles.push({
            baseX: column * spacing - spacing + offsetX,
            baseY: row * spacing - spacing * 0.45,
            x: 0,
            y: 0,
            radius: 1.8 + Math.random() * 2.6,
            phase: Math.random() * Math.PI * 2,
            speed: 0.55 + Math.random() * 0.7,
            drift: 10 + Math.random() * 20,
          });
        }
      }
    };

    const resize = () => {
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      width = window.innerWidth;
      height = window.innerHeight;
      canvas.width = Math.floor(width * dpr);
      canvas.height = Math.floor(height * dpr);
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      createParticles();
    };

    const handleMouseMove = (event) => {
      mouse.x = event.clientX;
      mouse.y = event.clientY;
      mouse.active = true;
    };

    const handleMouseLeave = () => {
      mouse.active = false;
      mouse.x = -1000;
      mouse.y = -1000;
    };

    const draw = (time) => {
      const t = time * 0.001;

      ctx.clearRect(0, 0, width, height);
      ctx.fillStyle = '#020617';
      ctx.fillRect(0, 0, width, height);

      const gradient = ctx.createRadialGradient(
        mouse.active ? mouse.x : width * 0.5,
        mouse.active ? mouse.y : height * 0.35,
        0,
        width * 0.5,
        height * 0.5,
        Math.max(width, height) * 0.72
      );
      gradient.addColorStop(0, 'rgba(59, 130, 246, 0.24)');
      gradient.addColorStop(0.42, 'rgba(124, 58, 237, 0.13)');
      gradient.addColorStop(1, 'rgba(2, 6, 23, 0)');
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, width, height);

      particles.forEach((particle) => {
        const waveX = Math.sin(t * particle.speed + particle.phase) * particle.drift;
        const waveY = Math.cos(t * (particle.speed * 0.8) + particle.phase) * particle.drift;
        let influenceX = 0;
        let influenceY = 0;

        if (mouse.active) {
          const dx = mouse.x - particle.baseX;
          const dy = mouse.y - particle.baseY;
          const distance = Math.hypot(dx, dy);
          const radius = 230;

          if (distance < radius) {
            const force = (1 - distance / radius) ** 2;
            const angle = Math.atan2(dy, dx);
            influenceX = Math.cos(angle) * force * 48;
            influenceY = Math.sin(angle) * force * 48;
          }
        }

        particle.x = particle.baseX + waveX + influenceX;
        particle.y = particle.baseY + waveY + influenceY;
      });

      for (let i = 0; i < particles.length; i += 1) {
        for (let j = i + 1; j < particles.length; j += 1) {
          const a = particles[i];
          const b = particles[j];
          const distance = Math.hypot(a.x - b.x, a.y - b.y);
          const maxDistance = 148;

          if (distance < maxDistance) {
            const opacity = (1 - distance / maxDistance) * 0.52;
            const pulse = 0.65 + Math.sin(t * 2 + a.phase + b.phase) * 0.35;

            ctx.strokeStyle = `rgba(126, 200, 255, ${opacity * pulse})`;
            ctx.lineWidth = 1;
            ctx.beginPath();
            ctx.moveTo(a.x, a.y);
            ctx.lineTo(b.x, b.y);
            ctx.stroke();
          }
        }
      }

      if (mouse.active) {
        particles.forEach((particle) => {
          const distance = Math.hypot(mouse.x - particle.x, mouse.y - particle.y);

          if (distance < 210) {
            const opacity = (1 - distance / 210) * 0.36;
            ctx.strokeStyle = `rgba(196, 181, 253, ${opacity})`;
            ctx.lineWidth = 1.2;
            ctx.beginPath();
            ctx.moveTo(mouse.x, mouse.y);
            ctx.lineTo(particle.x, particle.y);
            ctx.stroke();
          }
        });
      }

      particles.forEach((particle) => {
        const distanceToMouse = mouse.active
          ? Math.hypot(mouse.x - particle.x, mouse.y - particle.y)
          : 9999;
        const glow = Math.max(0, 1 - distanceToMouse / 190);

        ctx.shadowColor = glow > 0 ? 'rgba(196, 181, 253, 0.9)' : 'rgba(96, 165, 250, 0.55)';
        ctx.shadowBlur = 12 + glow * 20;
        ctx.fillStyle = glow > 0.15
          ? `rgba(216, 180, 254, ${0.76 + glow * 0.24})`
          : 'rgba(147, 197, 253, 0.78)';
        ctx.beginPath();
        ctx.arc(particle.x, particle.y, particle.radius + glow * 2.4, 0, Math.PI * 2);
        ctx.fill();
      });

      ctx.shadowBlur = 0;
      animationFrame = window.requestAnimationFrame(draw);
    };

    resize();
    window.addEventListener('resize', resize);
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseleave', handleMouseLeave);
    animationFrame = window.requestAnimationFrame(draw);

    return () => {
      window.cancelAnimationFrame(animationFrame);
      window.removeEventListener('resize', resize);
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, []);

  return <canvas ref={canvasRef} className="login-network-canvas" aria-hidden="true" />;
}

function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  
  const { login, isAuthenticated } = useAuthStore();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Redirect if already authenticated
    if (isAuthenticated) {
      const from = location.state?.from?.pathname || '/';
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, navigate, location]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      await login(email, password);
      const from = location.state?.from?.pathname || '/';
      navigate(from, { replace: true });
    } catch (err) {
      setError(
        err.response?.data?.error?.message ||
        'Přihlášení se nezdařilo. Zkontrolujte prosím své údaje.'
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="login-hero min-h-screen flex flex-col justify-center overflow-hidden py-12 sm:px-6 lg:px-8">
      <NetworkBackground />
      <div className="login-hero__glow login-hero__glow--blue" />
      <div className="login-hero__glow login-hero__glow--violet" />

      <div className="relative z-10 sm:mx-auto sm:w-full sm:max-w-2xl">
        <div className="text-center px-6">
          <h1 className="text-6xl sm:text-7xl lg:text-8xl font-black tracking-[0.18em] text-white drop-shadow-[0_0_34px_rgba(96,165,250,0.6)]">
            IT.CAFE
          </h1>
          <p className="mt-4 text-2xl sm:text-3xl font-semibold tracking-[0.34em] text-blue-100">
            MEETING ROOM
          </p>
          <p className="mt-7 text-xl sm:text-2xl font-medium text-violet-100">
            Rezervační systém
          </p>
          <p className="mt-2 text-sm sm:text-base tracking-[0.18em] uppercase text-blue-200/80">
            Řipská 15, Praha 3
          </p>
        </div>
      </div>

      <div className="relative z-10 mt-10 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-slate-950/75 py-8 px-4 shadow-2xl shadow-blue-950/40 ring-1 ring-blue-300/20 rounded-2xl backdrop-blur-xl sm:px-10">
          <form className="space-y-6" onSubmit={handleSubmit}>
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-blue-100">
                Email
              </label>
              <div className="mt-1 relative">
                <FiMail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-blue-200/70" />
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full pl-10 pr-3 py-2 border border-blue-300/20 rounded-lg bg-slate-900/80 text-white placeholder:text-slate-400 shadow-sm focus:outline-none focus:ring-2 focus:ring-violet-400 focus:border-violet-300"
                  placeholder="vase@email.cz"
                />
              </div>
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-blue-100">
                Heslo
              </label>
              <div className="mt-1 relative">
                <FiLock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-blue-200/70" />
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full pl-10 pr-10 py-2 border border-blue-300/20 rounded-lg bg-slate-900/80 text-white placeholder:text-slate-400 shadow-sm focus:outline-none focus:ring-2 focus:ring-violet-400 focus:border-violet-300"
                  placeholder="Zadejte heslo"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-blue-200/70 hover:text-white"
                >
                  {showPassword ? <FiEyeOff /> : <FiEye />}
                </button>
              </div>
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg text-sm">
                {error}
              </div>
            )}

            <div className="flex items-center justify-between">
              <div className="text-sm">
                <Link to="/register" className="font-medium text-blue-200 hover:text-violet-200">
                  Nemáte účet? Zaregistrujte se
                </Link>
              </div>
            </div>

            <div>
              <button
                type="submit"
                disabled={isLoading}
                className="w-full flex justify-center py-3 px-4 border border-transparent rounded-lg shadow-lg shadow-violet-950/40 text-sm font-semibold text-white bg-gradient-to-r from-blue-500 to-violet-600 hover:from-blue-400 hover:to-violet-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-slate-950 focus:ring-violet-300 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isLoading ? (
                  <span className="flex items-center">
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Přihlašuji...
                  </span>
                ) : (
                  'Přihlásit se'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default LoginPage;
